'use client';

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import type { Employee, EmployeeEmergencyContact } from './types';

interface EmergencyContactModalProps {
  employee: Employee | null;
  existingContact: EmployeeEmergencyContact | null;
  onClose: () => void;
  onSaved: () => void;
}

const RELATIONSHIPS = ['Spouse', 'Partner', 'Parent', 'Sibling', 'Child', 'Friend', 'Other Relative', 'Other'];

const EmergencyContactModal = ({ employee, existingContact, onClose, onSaved }: EmergencyContactModalProps) => {
  const isEdit = !!existingContact;
  const [contactName, setContactName] = useState('');
  const [relationship, setRelationship] = useState('Spouse');
  const [primaryPhone, setPrimaryPhone] = useState('');
  const [secondaryPhone, setSecondaryPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const supabase = createClient();

  useEffect(() => {
    if (!employee) return;
    setSubmitting(false);
    if (existingContact) {
      setContactName(existingContact.contact_name ?? '');
      setRelationship(existingContact.relationship ?? 'Spouse');
      setPrimaryPhone(existingContact.primary_phone ?? '');
      setSecondaryPhone(existingContact.secondary_phone ?? '');
      setEmail(existingContact.email ?? '');
      setIsPrimary(existingContact.is_primary ?? false);
      setNotes(existingContact.notes ?? '');
    } else {
      setContactName(''); setRelationship('Spouse'); setPrimaryPhone('');
      setSecondaryPhone(''); setEmail(''); setIsPrimary(false); setNotes('');
    }
  }, [employee?.id, existingContact?.id]);

  useEffect(() => {
    if (!employee) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [employee, onClose]);

  if (!employee) return null;

  const isValid = contactName.trim().length >= 2 && primaryPhone.trim().length >= 7 && !!relationship && !submitting;

  const saveWithRetry = async () => {
    if (!isValid) return;
    setSubmitting(true);
    const payload: any = {
      employee_id: employee.id,
      contact_name: contactName.trim(),
      relationship,
      primary_phone: primaryPhone.trim(),
      secondary_phone: secondaryPhone.trim() || null,
      email: email.trim() || null,
      is_primary: isPrimary,
      notes: notes.trim() || null,
      updated_at: new Date().toISOString(),
    };
    console.log(`[EmergencyContact] Saving (${isEdit ? 'edit' : 'create'}):`, payload);

    for (let attempt = 0; attempt < 3; attempt++) {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Save timed out (attempt ${attempt + 1}/3)`)), 8000));
      const opPromise = isEdit
        ? supabase.from('employee_emergency_contacts').update(payload).eq('id', existingContact!.id)
            .then((res: any) => { if (res.error) throw res.error; return res.data; })
        : supabase.from('employee_emergency_contacts').insert(payload)
            .then((res: any) => { if (res.error) throw res.error; return res.data; });

      try {
        if (isPrimary) {
          await supabase.from('employee_emergency_contacts')
            .update({ is_primary: false })
            .eq('employee_id', employee.id)
            .neq('id', existingContact?.id ?? '00000000-0000-0000-0000-000000000000');
        }
        await Promise.race([opPromise, timeoutPromise]);
        console.log(`[EmergencyContact] Save succeeded attempt ${attempt + 1}`);
        toast.success(isEdit ? 'Contact updated' : 'Contact added', { duration: 2500, icon: '☎️' });
        onSaved();
        setSubmitting(false);
        onClose();
        return;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
        const isTimeout = msg.includes('timed out');
        console.warn(`[EmergencyContact] Attempt ${attempt + 1} failed: ${msg}`);
        if (attempt === 2 || (!isRateLimit && !isTimeout)) {
          toast.error(`Save failed: ${msg}`, { duration: 6000 });
          setSubmitting(false);
          return;
        }
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
      }
    }
  };

  const handleDelete = async () => {
    if (!existingContact) return;
    if (!confirm(`Delete emergency contact "${existingContact.contact_name}"?`)) return;
    setSubmitting(true);

    console.log(`[EmergencyContact] Deleting:`, existingContact.id);

    for (let attempt = 0; attempt < 3; attempt++) {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Delete timed out (attempt ${attempt + 1}/3)`)), 8000));
      const opPromise = supabase.from('employee_emergency_contacts').delete().eq('id', existingContact.id)
        .then((res: any) => { if (res.error) throw res.error; return res.data; });

      try {
        await Promise.race([opPromise, timeoutPromise]);
        console.log(`[EmergencyContact] Delete succeeded attempt ${attempt + 1}`);
        toast.success('Contact removed', { icon: '🗑', duration: 2500 });
        onSaved();
        setSubmitting(false);
        onClose();
        return;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
        const isTimeout = msg.includes('timed out');
        console.warn(`[EmergencyContact] Delete attempt ${attempt + 1} failed: ${msg}`);
        if (attempt === 2 || (!isRateLimit && !isTimeout)) {
          toast.error(`Delete failed: ${msg}`, { duration: 6000 });
          setSubmitting(false);
          return;
        }
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-[#1C2E5E]">{isEdit ? 'Edit Emergency Contact' : 'Add Emergency Contact'}</h3>
            <p className="text-[12px] text-gray-500 mt-0.5">{employee.first_name} {employee.last_name}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/></svg>
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Contact Name <span className="text-red-500">*</span></label>
            <input type="text" value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Full name" maxLength={120} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Relationship <span className="text-red-500">*</span></label>
            <select value={relationship} onChange={e => setRelationship(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all">
              {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Primary Phone <span className="text-red-500">*</span></label>
              <input type="tel" value={primaryPhone} onChange={e => setPrimaryPhone(e.target.value)} placeholder="(937) 555-0100" className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Secondary Phone</label>
              <input type="tel" value={secondaryPhone} onChange={e => setSecondaryPhone(e.target.value)} placeholder="optional" className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="optional" className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} maxLength={500} placeholder="Best time to call, preferred language, etc." className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all resize-none" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} className="rounded" />
            <span className="text-[12px] text-gray-700">Mark as primary emergency contact</span>
          </label>
        </div>
        <div className="flex items-center justify-between gap-2 p-5 border-t border-gray-100">
          {isEdit ? (
            <button type="button" onClick={handleDelete} className="text-[12px] text-red-600 hover:underline font-semibold">Delete contact</button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
            <button type="button" onClick={saveWithRetry} disabled={!isValid} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-[#1C2E5E] hover:bg-[#022b80] disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Contact'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmergencyContactModal;
